/**
 * EtlUploadPanel — startet den Upload einer Punktwolken-Rohdatei (e57/LAS/LAZ)
 * an den dem aktuellen Projekt zugeordneten ETL-Server (Proxy via orto-api).
 *
 * Token bleibt server-seitig; der Browser lädt nur an den orto-api-Proxy.
 */

import { useEffect, useState, useRef } from 'react'
import { apiFetch, API_URL } from '../../lib/apiBase'
import { useAuthStore } from '../../store/auth'

interface Workflow { id: string; name: string; description?: string | null }
interface Job { id: string; status: string; error?: string | null; finishedAt?: string | null }

const ACCEPTED = '.e57,.las,.laz,.ply,.xyz'

export function EtlUploadPanel() {
  const currentProject = useAuthStore((s) => s.currentProject)
  const projectId = currentProject?.id
  const etlServer = (currentProject as any)?.etlServer as { id: string; name: string; apiUrl: string } | null | undefined

  const [open, setOpen] = useState(false)
  const [workflows, setWorkflows] = useState<Workflow[]>([])
  const [workflowId, setWorkflowId] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [phase, setPhase] = useState<'idle' | 'uploading' | 'starting' | 'running' | 'done' | 'error'>('idle')
  const [progress, setProgress] = useState(0)
  const [job, setJob] = useState<Job | null>(null)
  const [errMsg, setErrMsg] = useState('')
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const pollTimer = useRef<number | null>(null)

  useEffect(() => () => { if (pollTimer.current) window.clearTimeout(pollTimer.current) }, [])

  // Workflows laden, wenn Modal aufgeht
  useEffect(() => {
    if (!open || !projectId || !etlServer) return
    apiFetch(`/api/projects/${projectId}/etl/workflows`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(json => {
        const list: Workflow[] = json.data || json || []
        setWorkflows(list)
        if (list.length && !workflowId) setWorkflowId(list[0].id)
      })
      .catch(() => setWorkflows([]))
  }, [open, projectId, etlServer])

  const reset = () => {
    setFile(null); setPhase('idle'); setProgress(0); setJob(null); setErrMsg('')
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (pollTimer.current) { window.clearTimeout(pollTimer.current); pollTimer.current = null }
  }

  const close = () => { setOpen(false); reset() }

  // Polling: GET job status
  const pollJob = async (jobId: string) => {
    if (!projectId) return
    try {
      const r = await apiFetch(`/api/projects/${projectId}/etl/jobs/${jobId}`)
      const json = await r.json()
      const j: Job = json.data || json
      setJob(j)
      if (j.status === 'succeeded') { setPhase('done'); return }
      if (j.status === 'failed' || j.status === 'cancelled') {
        setPhase('error'); setErrMsg(j.error || `Job ${j.status}`); return
      }
      pollTimer.current = window.setTimeout(() => pollJob(jobId), 3000)
    } catch (e: any) {
      setPhase('error'); setErrMsg(e?.message || 'Status-Abfrage fehlgeschlagen')
    }
  }

  const startUpload = async () => {
    if (!file || !projectId || !workflowId) return
    setPhase('uploading'); setProgress(0); setErrMsg('')

    try {
      // Upload + Job-Start in einem Schritt: orto-api-Proxy macht TUS und finalize
      // mit workflowIds → ETL-Server startet die passenden Jobs automatisch.
      const result = await new Promise<any>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        const params = new URLSearchParams({
          filename: file.name,
          workflowIds: workflowId,
        })
        const url = `${API_URL}/api/projects/${projectId}/etl/upload?${params.toString()}`
        xhr.open('POST', url, true)
        xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('accessToken') || ''}`)
        xhr.setRequestHeader('Content-Type', 'application/octet-stream')
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100))
        }
        xhr.onload = () => {
          try {
            const j = JSON.parse(xhr.responseText)
            if (xhr.status >= 200 && xhr.status < 300) resolve(j.data || j)
            else reject(new Error(j?.error?.message || `HTTP ${xhr.status}`))
          } catch { reject(new Error(`HTTP ${xhr.status}`)) }
        }
        xhr.onerror = () => reject(new Error('Netzwerkfehler beim Upload'))
        xhr.send(file)
      })

      const startedJobIds: string[] = Array.isArray(result?.startedJobIds) ? result.startedJobIds : []
      if (startedJobIds.length === 0) {
        throw new Error(`ETL-Server hat keinen Job gestartet (MediaType "${result?.mediaTypeId || 'unbekannt'}" passt nicht zum gewählten Workflow)`)
      }

      const jobId = startedJobIds[0]
      const j: Job = { id: jobId, status: 'queued' }
      setJob(j); setPhase('running')
      pollJob(jobId)
    } catch (e: any) {
      setPhase('error'); setErrMsg(e?.message || 'Upload fehlgeschlagen')
    }
  }

  if (!projectId) return null

  return (
    <>
      <div className="ghost-section" data-section="etl-upload">
        <div className="ghost-section__header" style={{ cursor: 'default' }}>
          <span style={{ flex: 1, fontWeight: 500 }}>Datei-Konvertierung (ETL)</span>
          <button
            onClick={() => setOpen(true)}
            disabled={!etlServer}
            title={etlServer ? `ETL-Server: ${etlServer.name}` : 'Kein ETL-Server für dieses Projekt konfiguriert'}
            style={{
              padding: '4px 10px',
              border: '1px solid #1a1a1a',
              borderRadius: 4,
              background: etlServer ? '#1a1a1a' : '#9ca3af',
              color: '#fff',
              cursor: etlServer ? 'pointer' : 'not-allowed',
              fontSize: 12,
            }}
          >
            + Upload
          </button>
        </div>
        {!etlServer && (
          <div style={{ padding: '6px 10px', fontSize: 11, color: '#888' }}>
            Kein ETL-Server zugewiesen. Im Admin festlegen.
          </div>
        )}
        {etlServer && (
          <div style={{ padding: '6px 10px', fontSize: 11, color: '#666' }}>
            ETL: {etlServer.name}
          </div>
        )}
      </div>

      {open && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
          onClick={close}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 8, padding: 24, width: '90%', maxWidth: 560,
              boxShadow: '0 20px 50px rgba(0,0,0,0.3)', fontFamily: 'system-ui, sans-serif',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Punktwolke konvertieren</h3>
              <button onClick={close} style={{ border: 0, background: 'transparent', cursor: 'pointer', fontSize: 20 }}>×</button>
            </div>

            {phase === 'idle' && (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>Workflow</label>
                  <select
                    value={workflowId}
                    onChange={(e) => setWorkflowId(e.target.value)}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: 4 }}
                  >
                    {workflows.length === 0 && <option value="">— keine Workflows verfügbar —</option>}
                    {workflows.map(w => (
                      <option key={w.id} value={w.id}>{w.name}{w.description ? ` — ${w.description}` : ''}</option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>Datei (e57, LAS, LAZ, …)</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED}
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: 4 }}
                  />
                  {file && (
                    <p style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                      {file.name} ({(file.size / 1024 / 1024).toFixed(1)} MB)
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                  <button onClick={close} style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: 'pointer' }}>
                    Abbrechen
                  </button>
                  <button
                    onClick={startUpload}
                    disabled={!file || !workflowId}
                    style={{
                      padding: '8px 16px', border: 0, borderRadius: 4,
                      background: file && workflowId ? '#1a1a1a' : '#ccc', color: '#fff',
                      cursor: file && workflowId ? 'pointer' : 'not-allowed',
                    }}
                  >
                    Hochladen & Starten
                  </button>
                </div>
              </>
            )}

            {phase === 'uploading' && (
              <div>
                <p style={{ marginTop: 0 }}>Datei wird hochgeladen …</p>
                <div style={{ background: '#eee', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                  <div style={{ background: '#1a1a1a', height: '100%', width: `${progress}%`, transition: 'width 0.2s' }} />
                </div>
                <p style={{ fontSize: 12, color: '#666', textAlign: 'right', marginBottom: 0 }}>{progress}%</p>
              </div>
            )}

            {phase === 'starting' && <p>Job wird gestartet …</p>}

            {phase === 'running' && job && (
              <div>
                <p style={{ marginTop: 0 }}>Konvertierung läuft …</p>
                <p style={{ fontSize: 12, color: '#666' }}>Job-ID: <code>{job.id}</code></p>
                <p style={{ fontSize: 12, color: '#666' }}>Status: <strong>{job.status}</strong></p>
                <p style={{ fontSize: 11, color: '#888' }}>Status wird alle 3 Sekunden abgefragt.</p>
              </div>
            )}

            {phase === 'done' && (
              <div>
                <p style={{ marginTop: 0, color: '#16a34a' }}>✓ Konvertierung abgeschlossen</p>
                {job && <p style={{ fontSize: 12, color: '#666' }}>Job-ID: <code>{job.id}</code></p>}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                  <button onClick={close} style={{ padding: '8px 16px', border: 0, borderRadius: 4, background: '#1a1a1a', color: '#fff', cursor: 'pointer' }}>
                    Schließen
                  </button>
                </div>
              </div>
            )}

            {phase === 'error' && (
              <div>
                <p style={{ marginTop: 0, color: '#dc2626' }}>Fehler: {errMsg}</p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                  <button onClick={reset} style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: 'pointer' }}>
                    Erneut versuchen
                  </button>
                  <button onClick={close} style={{ padding: '8px 16px', border: 0, borderRadius: 4, background: '#1a1a1a', color: '#fff', cursor: 'pointer' }}>
                    Schließen
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
